import type { ApiFlow } from '../types'

export const nativeEmbeddedLoginFlow: ApiFlow = {
  id: 'native.embedded.login',
  appTypes: ['native'],
  title: 'Embedded sign-in (native)',
  summary:
    'Call /auth/start with client_mode=native, loop on /auth/continue while status is pending, then exchange the authorization code at /oauth2/token.',
  steps: [
    {
      id: 'start',
      title: 'Start login',
      when: 'User taps Sign in in your app',
      method: 'POST',
      path: '/api/v1/auth/start',
      operationId: 'authStart',
      headers: {
        'Content-Type': 'application/json',
        Origin: '<your-app-origin>',
      },
      requestBody: {
        method: 'password',
        identifier: 'user@example.com',
        password: '<user-password>',
        client_mode: 'native',
        code_challenge: '<s256-challenge>',
        code_challenge_method: 'S256',
      },
      responseVariants: [
        {
          label: 'pending (MFA)',
          status: 200,
          body: {
            data: {
              status: 'pending',
              flow_id: 'flw_01HXXXXXXXXXXXXXXXXXXXXXX',
              pause: { kind: 'mfa.picker', factors: [{ id: 'totp', label: 'Authenticator app' }] },
            },
          },
          nextStepId: 'continue',
        },
        {
          label: 'success',
          status: 200,
          body: {
            data: {
              status: 'success',
              code: '<auth-code>',
              state: '<csrf-state>',
              redirect_uri: '{{REDIRECT_URI}}',
            },
          },
          nextStepId: 'token',
        },
        {
          label: 'invalid credentials',
          status: 401,
          body: {
            error: { code: 'INVALID_CREDENTIALS', message: 'Invalid identifier or password.' },
          },
        },
      ],
      pauseKinds: [
        'mfa.picker',
        'mfa.verify',
        'channel_otp_send',
        'captcha',
        'federation_redirect',
      ],
    },
    {
      id: 'continue',
      title: 'Continue loop',
      when: 'Prior response had status: pending',
      method: 'POST',
      path: '/api/v1/auth/continue',
      operationId: 'authContinue',
      headers: { 'Content-Type': 'application/json' },
      requestBody: {
        flow_id: 'flw_01HXXXXXXXXXXXXXXXXXXXXXX',
        client_mode: 'native',
        mfa_factor_id: 'totp',
      },
      responseVariants: [
        {
          label: 'pending',
          status: 200,
          body: {
            data: {
              status: 'pending',
              flow_id: 'flw_01HXXXXXXXXXXXXXXXXXXXXXX',
              pause: { kind: 'mfa.verify', factor: 'totp' },
            },
          },
          nextStepId: 'continue',
          loop: true,
        },
        {
          label: 'success',
          status: 200,
          body: {
            data: {
              status: 'success',
              code: '<auth-code>',
              state: '<csrf-state>',
              redirect_uri: '{{REDIRECT_URI}}',
            },
          },
          nextStepId: 'token',
        },
        {
          label: 'flow expired',
          status: 410,
          body: { error: { code: 'FLOW_EXPIRED', message: 'Flow expired.' } },
        },
      ],
      pauseKinds: ['mfa.verify', 'channel_otp_send', 'captcha', 'credential_set'],
    },
    {
      id: 'token',
      title: 'Exchange code for tokens',
      when: 'Start or continue returned status: success with code',
      method: 'POST',
      path: '/oauth2/token',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      requestBody: {
        grant_type: 'authorization_code',
        code: '<code-from-prior-step>',
        redirect_uri: '{{REDIRECT_URI}}',
        client_id: '{{CLIENT_ID}}',
        code_verifier: '<original-verifier>',
      },
      responseVariants: [
        {
          label: 'success',
          status: 200,
          body: {
            access_token: 'eyJ...',
            token_type: 'bearer',
            expires_in: 3600,
            refresh_token: '...',
            id_token: 'eyJ...',
          },
        },
      ],
    },
  ],
}
